/**
 * Application Configuration
 *
 * Centralized configuration management using runtime hostname detection.
 * This allows a single build artifact to work across all environments.
 */

export class AppConfig {
  private static instance: AppConfig

  private constructor() {}

  static getInstance(): AppConfig {
    if (!AppConfig.instance) {
      AppConfig.instance = new AppConfig()
    }
    return AppConfig.instance
  }

  /**
   * Get the API base URL based on the current environment
   * Detects environment from window.location.hostname
   */
  getApiUrl(): string {
    // Server-side rendering: use env var or localhost for build/SSR
    if (typeof window === 'undefined') {
      return import.meta.env.VITE_API_URL || 'http://localhost:3001'
    }

    const { hostname } = window.location

    // Production: app.vectorsell.com
    if (hostname === 'app.vectorsell.com') {
      return 'https://app.vectorsell.com/api'
    }

    // Preview: preview.app.vectorsell.com
    if (hostname === 'preview.app.vectorsell.com' || (hostname.includes('preview') && hostname.includes('vectorsell'))) {
      return 'https://preview.app.vectorsell.com/api'
    }

    // Cloudflare Pages preview domains (*.pages.dev)
    if (hostname.endsWith('.pages.dev')) {
      return hostname.includes('preview')
        ? 'https://preview.app.vectorsell.com/api'
        : 'https://app.vectorsell.com/api'
    }

    // Local development
    return 'http://localhost:3001'
  }

  /**
   * Get the current environment name
   */
  getEnvironment(): 'production' | 'preview' | 'development' {
    if (typeof window === 'undefined') {
      return 'development'
    }

    const { hostname } = window.location

    if (hostname === 'app.vectorsell.com') {
      return 'production'
    }

    if (hostname === 'preview.app.vectorsell.com' || hostname.includes('preview')) {
      return 'preview'
    }

    return 'development'
  }

  /**
   * Check if running in production
   */
  isProduction(): boolean {
    return this.getEnvironment() === 'production'
  }

  /**
   * Check if running in preview
   */
  isPreview(): boolean {
    return this.getEnvironment() === 'preview'
  }

  /**
   * Check if running in development
   */
  isDevelopment(): boolean {
    return this.getEnvironment() === 'development'
  }

  /**
   * Get the R2 public URL for static assets
   * Returns the configured CDN URL or falls back to local paths
   */
  getR2PublicUrl(): string | null {
    // R2 public URL (Cloudflare CDN)
    if (typeof window === 'undefined') {
      return import.meta.env.VITE_R2_PUBLIC_URL || null
    }

    const { hostname } = window.location

    // Production and preview use CDN
    if (hostname.includes('vectorsell.com') || hostname.endsWith('.pages.dev')) {
      return 'https://static-sk.futuresting.com'
    }

    // Local development: use local paths
    return null
  }

  /**
   * Get app-wide configuration
   */
  getConfig() {
    return {
      apiUrl: this.getApiUrl(),
      environment: this.getEnvironment(),
      r2PublicUrl: this.getR2PublicUrl(),
      appName: import.meta.env.VITE_APP_NAME || 'Sika AI - Project Proposal Generator',
      currency: import.meta.env.VITE_CURRENCY || 'RON',
      vatRate: parseInt(import.meta.env.VITE_VAT_RATE || '19', 10),
    }
  }
}

// Export singleton instance
export const config = AppConfig.getInstance()
